import { useState } from "react";
import { BrandMark } from "./BrandMark";
import { Button } from "./Button";
import { Container } from "./Container";
import styles from "./AppHeader.module.css";
import { formatTodayUtc } from "../lib/format";
import { useDeskView } from "../desk/useDeskView";
import { useSession } from "../session/useSession";

/**
 * The desk header.
 *
 * Sign-out is **not** here. `APP-017` put it in this row, and its reasoning was real — it is
 * the one control a founder may need in a hurry, on a borrowed laptop, in front of a room —
 * but the row it was in did not survive a phone. `APP-022` moved it to the footer, beside
 * the theme control, and `AppFooter` carries the whole of that argument and what answers it.
 * That was a design-record departure (`contracts/design/app-desk/README.md` D7) rather than a
 * canon decision, which is what made it the operator's to reverse.
 *
 * What is left in the row is the identity, canon § 8's UTC date line, and the one action a
 * founder came here to take.
 */

/**
 * The desk's one persistent action, in the masthead (`APP-017`).
 *
 * `contracts/design/app-desk/` draws it in the identity row at 390 and on the tab row at
 * 1366; the build previously kept it in `DeskNav` at both widths (design record, departure
 * D7) because `AppHeader` renders outside `SessionGate` and a submit action means nothing
 * to somebody who is not signed in. That objection is answered by gating the control on the
 * session — it renders only while there is one, so the sign-in, session-pending and
 * session-error screens keep their single control. `AppFooter`'s sign-out does the same.
 *
 * A button rather than a link: it opens a form on the deals view in place, and `openSubmit`
 * moves the address itself. A link would promise a destination the press does not go to.
 */
function SubmitDealControl() {
  const { state } = useSession();
  const { openSubmit } = useDeskView();

  if (state.status !== "signed-in") return null;

  return <Button onClick={openSubmit}>Submit a deal</Button>;
}

export function AppHeader() {
  /*
   * Sampled once, when the desk mounts (`APP-016`).
   *
   * The date changes at 00:00 UTC and this is not a countdown, so subscribing to the clock
   * store for it would keep a one-second interval alive to repaint the same string 86,399
   * times a day. A desk left open across the boundary shows yesterday's date until it is
   * reloaded; the sentence beside it — which is the part canon § 8 obliges — stays true
   * either way, and every panel that prints a date prints core's, not this one.
   */
  const [today] = useState(() => formatTodayUtc(Date.now()));

  return (
    <header className={styles.header}>
      <a className={styles.skipLink} href="#main">
        Skip to content
      </a>
      <Container className={styles.inner}>
        <span className={styles.identity}>
          {/* Not a link: "/" is the desk itself when signed in and the sign-in surface when
              not, so a wordmark link would go nowhere a founder is not already. */}
          <span className={styles.wordmark}>
            {/* The product's mark, hidden from the accessibility tree because the wordmark
                beside it already names the identity. `APP-015` put it here in place of the
                rank-01 tile, which canon decision `D-013` withdrew: the identity and the
                board's first position were the same object, so the desk wore a rank. */}
            <BrandMark className={styles.mark} />
            Dealbid
          </span>
          <span className={styles.surface}>Seller</span>
        </span>
        {/*
          * Canon § 8's statement, in the masthead the canvas puts it in. The drawing carries
          * it on the four desktop screens and drops it at 390; the build keeps it at both
          * widths and wraps it onto its own line instead (design record, departure D6).
          */}
        <p className={`${styles.today} figure`}>
          <span>{today}</span>
          <span aria-hidden="true"> · </span>
          <span>every date on this desk is UTC</span>
        </p>
        {/*
          * `Submit a deal`, alone (`APP-022`). The colour-theme control left this row for the
          * footer in `APP-017` — a preference spending masthead width — and sign-out has now
          * followed it for the neighbouring reason: at 390px the row could not hold two
          * controls, an identity and a date line, and the date line is canon § 8's.
          */}
        <div className={styles.controls}>
          <SubmitDealControl />
        </div>
      </Container>
    </header>
  );
}
